import React, { useCallback, useEffect, useState } from "react";
import { api, type ManagedUser, type UserRole } from "../api";

type Props = {
  showToast: (kind: "success" | "error" | "info", message: string) => void;
};

export const Users: React.FC<Props> = ({ showToast }) => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [envAdmin, setEnvAdmin] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Anlege-Formular
  const [newName, setNewName] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newRole, setNewRole] = useState<UserRole>("editor");
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const r = await api.usersList();
      setUsers(r.users);
      setEnvAdmin(r.envAdmin);
    } catch (e) {
      showToast("error", `Benutzer konnten nicht geladen werden: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      await api.userCreate(newName.trim(), newPass, newRole);
      showToast("success", `Benutzer „${newName.trim()}" angelegt.`);
      setNewName("");
      setNewPass("");
      setNewRole("editor");
      await refresh();
    } catch (err) {
      showToast("error", (err as Error).message.replace(/^\d+ [^:]+: /, ""));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u: ManagedUser): Promise<void> => {
    setBusyId(u.id);
    try {
      await api.userUpdate(u.id, { active: !u.active });
      showToast("success", `„${u.username}" ${u.active ? "deaktiviert" : "aktiviert"}.`);
      await refresh();
    } catch (err) {
      showToast("error", (err as Error).message.replace(/^\d+ [^:]+: /, ""));
    } finally {
      setBusyId(null);
    }
  };

  const changeRole = async (u: ManagedUser, role: UserRole): Promise<void> => {
    if (role === u.role) return;
    setBusyId(u.id);
    try {
      await api.userUpdate(u.id, { role });
      showToast("success", `Rolle von „${u.username}" → ${role}.`);
      await refresh();
    } catch (err) {
      showToast("error", (err as Error).message.replace(/^\d+ [^:]+: /, ""));
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (u: ManagedUser): Promise<void> => {
    const pw = window.prompt(`Neues Passwort für „${u.username}" (mind. 6 Zeichen):`);
    if (pw === null) return;
    if (pw.length < 6) {
      showToast("error", "Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    setBusyId(u.id);
    try {
      await api.userSetPassword(u.id, pw);
      showToast("success", `Passwort für „${u.username}" gesetzt.`);
    } catch (err) {
      showToast("error", (err as Error).message.replace(/^\d+ [^:]+: /, ""));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (u: ManagedUser): Promise<void> => {
    if (!window.confirm(`Benutzer „${u.username}" wirklich löschen?`)) return;
    setBusyId(u.id);
    try {
      await api.userDelete(u.id);
      showToast("success", `„${u.username}" gelöscht.`);
      await refresh();
    } catch (err) {
      showToast("error", (err as Error).message.replace(/^\d+ [^:]+: /, ""));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="users-view">
      <div className="users-head">
        <h2>Benutzerverwaltung</h2>
        <p className="users-sub">
          Lege Team-Mitglieder an, aktiviere/deaktiviere Konten und setze Passwörter.
          {envAdmin ? (
            <>
              {" "}
              Der ENV-Admin <strong>{envAdmin}</strong> (aus <code>.env</code>) bleibt als Notfall-Zugang
              immer aktiv und erscheint nicht in der Liste.
            </>
          ) : null}
        </p>
      </div>

      <form className="users-create" onSubmit={(e) => void create(e)}>
        <div className="users-create-row">
          <label>
            Benutzername
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z. B. marie" disabled={creating} />
          </label>
          <label>
            Passwort
            <input
              type="password"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              placeholder="mind. 6 Zeichen"
              disabled={creating}
            />
          </label>
          <label>
            Rolle
            <select value={newRole} onChange={(e) => setNewRole(e.target.value as UserRole)} disabled={creating}>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <button type="submit" disabled={creating || newName.trim().length < 2 || newPass.length < 6}>
            {creating ? "Anlegen…" : "Benutzer anlegen"}
          </button>
        </div>
        <p className="users-hint">Admins dürfen ebenfalls Benutzer verwalten. Editoren nutzen nur das Studio.</p>
      </form>

      {loading ? (
        <p className="users-empty">Lade…</p>
      ) : users.length === 0 ? (
        <p className="users-empty">Noch keine verwalteten Benutzer. Lege oben den ersten an.</p>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>Rolle</th>
              <th>Status</th>
              <th>Angelegt</th>
              <th style={{ textAlign: "right" }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.active ? "" : "is-inactive"}>
                <td>
                  <strong>{u.username}</strong>
                  {u.isSelf ? <span className="users-tag">du</span> : null}
                </td>
                <td>
                  <select
                    value={u.role}
                    onChange={(e) => void changeRole(u, e.target.value as UserRole)}
                    disabled={busyId === u.id}
                  >
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <span className={`users-badge ${u.active ? "ok" : "off"}`}>{u.active ? "aktiv" : "inaktiv"}</span>
                </td>
                <td className="users-date">{(u.createdAt ?? "").slice(0, 10)}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="users-btn" onClick={() => void toggleActive(u)} disabled={busyId === u.id}>
                    {u.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                  <button className="users-btn" onClick={() => void resetPassword(u)} disabled={busyId === u.id}>
                    Passwort
                  </button>
                  <button className="users-btn danger" onClick={() => void remove(u)} disabled={busyId === u.id}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
